import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import { Pencil, PlusCircle, Star, Type } from "lucide-react";
import { Suspense } from "react";
import { toast } from "sonner";
import { createColumnConfigHelper } from "@/components/data-table-filter/core/filters";
import { Page } from "@/components/page";
import { SheetSkeleton } from "@/components/skeletons/sheet-skeleton";
import { DataTable } from "@/components/table/DataTable";
import { TablePagination } from "@/components/table/DataTablePagination";
import type { ColumnDefWithMeta } from "@/components/table/dataTable.types";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePersistedReactTable } from "@/hooks/use-persisted-table";
import { useRequiredRole } from "@/hooks/useRequiredRole";
import { cn } from "@/lib/clientUtils";
import type { RouterOutputs } from "@/lib/trpcClient";
import { Approvers } from "./approval/-components/approvers";

type PolicyType = RouterOutputs["approvalPolicy"]["list"][number];
const dtf = createColumnConfigHelper<PolicyType>();

const tableName = "policies";

const columnsConfig = [
  dtf
    .text()
    .id("Name")
    .accessor((row) => row.name)
    .displayName("Name")
    .icon(Type)
    .build(),
];
export const Route = createFileRoute("/_authed/_org/policies/approval")({
  component: RouteComponent,
  loader: ({ context: { queryClient, trpc } }) => {
    queryClient.ensureQueryData(trpc.approvalPolicy.list.queryOptions());
  },
});

function RouteComponent() {
  const location = useLocation();
  const navigate = Route.useNavigate();
  const { admin } = useRequiredRole();
  const { trpc, queryClient } = Route.useRouteContext();

  const { mutate: makeDefaultPolicy } = useMutation(
    trpc.approvalPolicy.makeDefault.mutationOptions({
      onError: (error) => {
        toast.error("Failed to set default policy", {
          description: error.message,
        });
      },
      onSuccess: () => {
        toast.success("Default policy updated");
        queryClient.invalidateQueries(trpc.approvalPolicy.list.queryOptions());
      },
    }),
  );

  const { data: policies, isLoading } = useQuery(
    trpc.approvalPolicy.list.queryOptions(),
  );

  // Define columns for the DataTable
  const tableColumns: ColumnDefWithMeta<PolicyType>[] = [
    {
      accessorFn: (row) => row.name,
      accessorKey: "Name",
      cell: ({ row }) => {
        const isDefault = row.original.isDefault;

        return (
          <div className="group flex items-center justify-between">
            <div className="flex flex-col">
              <span className="font-medium">{row.original.name}</span>
              <span className="font-light text-xs">
                {row.original.description}
              </span>
            </div>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={cn(
                    "ml-2 rounded-sm p-1 transition-colors duration-200",
                    isDefault ? "cursor-default" : "hover:bg-muted",
                  )}
                  disabled={isDefault}
                  onClick={() => {
                    if (!isDefault) {
                      makeDefaultPolicy({ policyId: row.original.id });
                    }
                  }}
                  type="button"
                >
                  <Star
                    className={cn(
                      "h-4 w-4 transition-all duration-200",
                      isDefault
                        ? "fill-yellow-400 text-yellow-400" // Visible yellow star when default
                        : "text-transparent group-hover:text-gray-400", // Hidden until hover, gray when hovered
                    )}
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {isDefault ? "Default policy" : "Make default"}
              </TooltipContent>
            </Tooltip>
          </div>
        );
      },
      enableGlobalFilter: true,
      id: "Name",
      meta: {
        type: "string",
      },
    },
    {
      accessorFn: (row) => row.approvers,
      cell: ({ row }) => (
        <Approvers
          approvers={row.original.approvers}
          groupOperator={row.original.groupOperator}
        />
      ),
      enableSorting: false,
      id: "Approvers",
      meta: {
        type: "string",
      },
    },
    {
      cell: ({ row }) => {
        return (
          <div className="flex justify-end gap-2">
            <Link
              params={{
                id: row.original.id,
              }}
              to="/policies/approval/$id/edit"
            >
              <Pencil className="h-4 w-4" />
            </Link>
          </div>
        );
      },
      id: "Actions",
      meta: {
        type: "string",
      },
    },
  ];

  const { table, actions, filters, columns, strategy } = usePersistedReactTable(
    {
      columns: tableColumns,
      data: policies ?? [],
      filterOptions: {
        columnsConfig,
        strategy: "client",
      },
      getRowId: (row) => row.id.toString(),
      tableName: tableName,
    },
  );

  return (
    <Sheet
      onOpenChange={(open) => {
        if (!open) {
          navigate({
            replace: true,
            to: "/policies/approval",
          });
        }
      }}
      open={
        location.pathname !== "/policies/approval/" &&
        location.pathname.includes("/policies/approval/")
      }
    >
      <Page
        heading="Approval Policies"
        actions={
          admin && (
            <Link to="/policies/approval/create">
              <Button>
                <PlusCircle />
                Create Policy
              </Button>
            </Link>
          )
        }
        table={{
          actions,
          columns,
          filters,
          strategy,
          table,
        }}
      >
        <DataTable
          noResultsComponent={
            <div className="flex h-36 w-full flex-col items-center justify-center space-y-4">
              <span className="text-xl">Matching No Policies</span>

              <Button onClick={actions?.removeAllFilters} variant={"outline"}>
                Clear Filters
              </Button>
            </div>
          }
          table={table}
          tableName={tableName}
          loading={isLoading}
          className="[&_th]:border-t-0!"
        />

        <TablePagination tableName={tableName} table={table} />
      </Page>

      <SheetContent
        className="w-full"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
      >
        <Suspense
          fallback={
            <div className="px-6">
              <SheetSkeleton />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </SheetContent>
    </Sheet>
  );
}
